import { Button, Icon, Stack, Text } from '@chakra-ui/react';
import { PlusIcon } from '@heroicons/react/24/outline';
import { PlanItem, PlanSection } from '@/lib/types';
import { blankSection } from '@/pages/planner/draft';
import { SortableList } from '@/components/sortable-list';
import SectionCard from './section-card';

interface SectionListProps {
  sections: PlanSection[];
  onChange: (sections: PlanSection[]) => void;
  onViewItem: (item: PlanItem) => void;
  showItemErrors?: boolean;
}

// SortableList requires an `id: UniqueIdentifier` field; PlanSection's
// stable client id is `uid` (string), so each section is wrapped for the
// list's bookkeeping only — `section` itself is passed through unchanged.
interface SortableSection {
  id: string;
  section: PlanSection;
}

// "2. Plan content" — ordered sections (drag-reorder via SortableList), each
// with its own independently-sortable item list (section-card.tsx). Cross-
// section item drag is out of scope (v2).
export default function SectionList({ sections, onChange, onViewItem, showItemErrors }: SectionListProps) {
  const updateSection = (section: PlanSection) => {
    onChange(sections.map((s) => (s.uid === section.uid ? section : s)));
  };

  const removeSection = (sectionUid: string) => {
    onChange(sections.filter((s) => s.uid !== sectionUid));
  };

  const addSection = () => {
    onChange([...sections, blankSection()]);
  };

  const sortableSections: SortableSection[] = sections.map((section) => ({
    id: section.uid,
    section,
  }));

  const handleReorder = (newItems: SortableSection[]) => {
    onChange(newItems.map((item) => item.section));
  };

  return (
    <Stack gap={4}>
      {sections.length === 0 && (
        <Text fontSize='sm' color='fg.subtle'>
          No sections yet. Add one to start building content.
        </Text>
      )}

      <SortableList
        items={sortableSections}
        onChange={handleReorder}
        renderItem={({ section }) => {
          const index = sections.findIndex((s) => s.uid === section.uid);
          return (
            <SortableList.Item id={section.uid}>
              <SectionCard
                section={section}
                index={index}
                onChange={updateSection}
                onRemove={() => removeSection(section.uid)}
                onViewItem={onViewItem}
                showItemErrors={showItemErrors}
              />
            </SortableList.Item>
          );
        }}
      />

      <Button variant='outline' onClick={addSection} alignSelf='flex-start'>
        <Icon as={PlusIcon} w={4} h={4} />
        Add section
      </Button>
    </Stack>
  );
}
